// BCM-898 · PA6 — Consumed batch variance / mismatch approval (slides 15-21). [Shekar]
// Status 40 or reviewVarianceReqd, AND approveFGConsumption, AND collabType !== "CMO".
// Renders the planned-vs-actual table INLINE (mockup slide 15); a "View
// Consumption Document" tab; top-right Sync/Resubmit. The Reject control is
// removed. BCM-744 config on the row decides whether the Consumed-Qty-Variance
// and Batches-Mismatch columns show.
//
// Stat-date variance (slide 20) and Produced-qty variance (slides 22-23) are
// each their OWN stacked section — StatDateVarianceSection / ProducedQtyVariance
// Section — not the shared VarianceMismatchDialog modal (that stays untouched;
// it is also used by the PO Collaboration dashboard —
// PurchaseOrderTabTable.jsx / VarianceMismatchButton.jsx).
//
// Multiple Variance/Mismatch Scenario (design slides 24-25): when more than one
// of the three sections applies, they stack one below the other in this order
// — Stat and Delivery Date Variance, Consumed Batch Variance/Mismatch, Produced
// Qty Variance — and share ONE common Approve button, rendered as its own
// block below ALL of them (not nested inside any single section's "Action
// Required" box). It hides itself while either the Consumed Batch or Produced
// Qty tab strip is on its "View Document"-style tab — approving doesn't apply
// while reviewing the underlying document. This file owns the single
// Approve/validation/payload/toast; the two sub-sections are controlled/
// presentational only.
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, CircularProgress, Box, Button,
} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import moment from "moment";
import SectionShell from "../components/SectionShell";
import ActionTabs from "../components/common/ActionTabs";
import ConsumptionDocumentTab from "../components/common/ConsumptionDocumentTab";
import SyncActions from "../components/common/SyncActions";
import ResubmitDocumentButton from "../components/common/ResubmitDocumentButton";
import StatDateVarianceSection, {
  shouldShowStatDateVariance, isStatDateEditable, isStatDateAutoAfterApproval, isEarlyDateVariance,
} from "./pa6/StatDateVarianceSection";
import ProducedQtyVarianceSection, {
  shouldShowProducedQtyVariance, isProducedQtyEditable, isProducedQtyAutoAfterApproval,
} from "./pa6/ProducedQtyVarianceSection";
import { fetchPlannedVsActual, roleUrl } from "../api/goodsReceiptApi";
import { useGRStyles } from "../goodsReceiptStyles";
import { useGRFeedback } from "../context/GRFeedbackContext";
import { trimQty } from "../utils/formatters";

const PA6VarianceMismatch = ({ row, detail, featuresAccess, viewOnly, onRefresh }) => {
  const classes = useGRStyles();
  const [alloc, setAlloc] = useState([]);
  // fetchComponentAllocationPlannedAndActualNewDirect returns these at the
  // TOP LEVEL of the response (res.data.cmoProductCode/cmoProductDescription),
  // not on the batch row — row (from fetchBatchesDirect) doesn't carry them.
  // Matches VarianceMismatchDialog.jsx, the reference implementation for this
  // same endpoint.
  const [cmoProductCode, setCmoProductCode] = useState(null);
  const [cmoProductDescription, setCmoProductDescription] = useState(null);
  // BCM-898 bug fix: also returned at the top level of this same response —
  // VarianceMismatchDialog.jsx (Batch Dashboard) uses this to decide whether
  // the Consumed Qty Variance (%) cell turns red; this file never captured it,
  // so that cell never had a limit to compare against at all.
  const [qtyVarianceAcceptanceLimit, setQtyVarianceAcceptanceLimit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState("");
  const feedback = useGRFeedback();
  const cancelRef = React.useRef(null);
  const approveCancelRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving PA6 variance mismatch");
      if (approveCancelRef.current) approveCancelRef.current.cancel("leaving PA6 variance mismatch");
    };
  }, []);

  // BCM-744 column visibility comes off the batch row (never recompute).
  const showVarianceCol = row.consumedQtyVarianceApprovalRequired || row.consumedQtyVarianceWarningMessageRequired;
  const showMismatchCol = row.lotMismatchApprovalRequired || row.lotMismatchWarningMessageRequired;
  // Each of these gates its own independent stacked section below.
  const showStatDateSection = shouldShowStatDateVariance(row);
  const showProducedQtySection = shouldShowProducedQtyVariance(row);

  // Lifted here (not in the sub-sections) — the single shared Approve button
  // needs the currently-edited values from whichever sections are showing.
  const [updateStatDate, setUpdateStatDate] = useState(moment());
  const [updatePOQty, setUpdatePOQty] = useState(row.producedQuantity ?? "");

  useEffect(() => {
    if (!row || !row.schrockenDeliveryScheduleBatchID) return;
    // A fresh row (new batch, or a refetch after Resubmit/Sync) means any
    // prior Approve is no longer relevant — drop the "Approved" button state.
    setApproved(false);
    setCmoProductCode(null);
    setCmoProductDescription(null);
    setQtyVarianceAcceptanceLimit(null);
    // fetchComponentAllocationPlannedAndActualNewDirect/{batchID}/{batchSize} —
    // batchSize, not poQuantity/targetQuantity/batchQty (those are a different
    // number and the endpoint returns an empty componentAllocationList against
    // them). Matches VarianceMismatchDialog's working call and the Slides &
    // Actions v3 spec for this endpoint (slides 13/15/17).
    const batchSize = row.batchSize || row.poQuantity || row.targetQuantity || row.batchQty || 0;
    setLoading(true);
    setError("");
    if (cancelRef.current) cancelRef.current.cancel("superseded PA6 fetch");
    const source = axios.CancelToken.source();
    cancelRef.current = source;
    fetchPlannedVsActual(row.schrockenDeliveryScheduleBatchID, batchSize, source.token)
      .then((res) => {
        if (!mountedRef.current) return;
        // res.data.data is the component array directly (VarianceMismatchDialog.jsx
        // and ApproveRejectConsumedBatch.jsx both confirm this shape) — there is no
        // nested componentAllocationList property to read here.
        if (res.data && res.data.status) {
          setAlloc(Array.isArray(res.data.data) ? res.data.data : []);
          setCmoProductCode(res.data.cmoProductCode || null);
          setCmoProductDescription(res.data.cmoProductDescription || null);
          setQtyVarianceAcceptanceLimit(res.data.qtyVarianceAcceptanceLimit ?? null);
        } else setError((res.data && res.data.message) || "Could not load consumption data.");
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        setError((err.response && err.response.data && err.response.data.message) || err.message);
        setLoading(false);
      });
  }, [row]);

  const mismatchCount = alloc.filter((c) => c.lotsMismatch === true || c.quantityVariancePresent === true).length;
  const canApprove = !viewOnly && featuresAccess && featuresAccess.approveFGConsumption !== false;
  // Gates the "Consumed Batch Variance/Mismatch" tab itself. Matches
  // VarianceMismatchDialog's own showConsumedBatchSection EXACTLY: a mismatch
  // existing in the data is not enough on its own — BCM-744 config must also
  // say approval is actually required for it (consumedQtyVarianceApprovalRequired
  // / lotMismatchApprovalRequired). When only the *WarningMessageRequired
  // flag is set, it's informational only — nothing to approve, so this
  // section (and its Approve button) must not appear, even though the
  // Consumed Qty Variance / Batches Mismatch COLUMNS still can (showVarianceCol
  // / showMismatchCol above are intentionally broader — they also honor the
  // warning-only flags).
  const hasQtyVariancePresent = alloc.some((c) => c.quantityVariancePresent === true);
  const hasLotMismatchPresent = alloc.some((c) => c.lotsMismatch === true);
  const showConsumedBatchSection =
    loading ||
    (row.consumedQtyVarianceApprovalRequired === true && hasQtyVariancePresent) ||
    (row.lotMismatchApprovalRequired === true && hasLotMismatchPresent);

  // Which inner tab the Consumed Batch and Produced Qty tab strips are
  // currently showing — the shared bottom Approve button hides itself while
  // either is on its "View Document"-style tab.
  const [consumedActiveTab, setConsumedActiveTab] = useState("variance");
  const [producedQtyActiveTab, setProducedQtyActiveTab] = useState("producedqty");
  const onDocumentTab = consumedActiveTab === "doc" || producedQtyActiveTab === "doc";

  const handleApprove = () => {
    if (showProducedQtySection && isProducedQtyEditable(row)) {
      const parsed = Number(String(updatePOQty ?? "").trim());
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setError("Please enter a valid positive number for Update SAP PO Qty before approving.");
        return;
      }
    }
    setApproving(true);
    setError("");
    const first = alloc[0] || {};
    const entry = {
      updateFromProcessFlow: true,
      updateStatusFromProcessFlow: true,
      statusID: 39,
      statusDescription: "Specs Approved",
      SASLBatchStatusID: 39,
      statusDisplayName: "Specs Approved",
      schrockenDeliveryScheduleBatchID: row.schrockenDeliveryScheduleBatchID,
    };
    if (showStatDateSection) {
      if (isStatDateEditable(row) && updateStatDate) {
        const parsedDate = moment(updateStatDate);
        if (parsedDate.isValid()) {
          const iso = parsedDate.toISOString();
          entry.statisticalDate = iso;
          entry.deliveryDate = iso;
          entry.committedDeliveryDate = iso;
        }
      } else if (isStatDateAutoAfterApproval(row) && isEarlyDateVariance(row)) {
        // Early + Automatic after approval → send the PRESENT DATE, matching
        // what's shown in the UI (defect: this used to echo the stale
        // statisticalDate/deliveryDate from SAP instead).
        const isoDate = moment().toISOString();
        entry.statisticalDate = isoDate;
        entry.deliveryDate = isoDate;
        entry.committedDeliveryDate = isoDate;
      } else if (isStatDateAutoAfterApproval(row)) {
        // Later + Automatic after approval → echo from the batch row, unchanged.
        entry.statisticalDate = row.statisticalDate ?? null;
        entry.deliveryDate = row.deliveryDate ?? null;
        entry.committedDeliveryDate = row.committedDeliveryDate ?? null;
      }
    }
    if (showProducedQtySection) {
      if (isProducedQtyEditable(row)) {
        const qty = Number(String(updatePOQty ?? "").trim());
        entry.producedQuantity = qty;
        entry.batchSize = qty;
      } else if (isProducedQtyAutoAfterApproval(row)) {
        // No control shown — echo the value already on the batch row.
        entry.producedQuantity = row.producedQuantity ?? null;
        entry.batchSize = row.producedQuantity ?? null;
      }
    }
    const payload = {
      sponsorName: row.sponsorName,
      BMSproductCode: row.fgMaterialCode,
      BMSproductDesc: row.fgMaterialName,
      CMOproductCode: cmoProductCode || row.cmoProductCode,
      CMOproductDesc: cmoProductDescription || row.cmoProductDescription,
      scheduleAgreementNumber: row.scheduleAgreementNumber,
      vendorBatchNumber: row.vendorBatchNumber,
      vendorMosymphonyID: row.vendorMosymphonyID,
      cmoBatchNumber: row.cmoBatchNumber,
      brandOwnerMosymphonyID: row.brandOwnerMosymphonyID,
      schrockenSLID: row.schrockenSLID,
      QtyVariance: first.quantityVarianceValue != null ? first.quantityVarianceValue : 0,
      lotMismatch: first.lotsMismatch != null ? first.lotsMismatch : false,
      quantityVariancePresent: first.quantityVariancePresent != null ? first.quantityVariancePresent : false,
      // Full BCM-744 flag set, same shape VarianceMismatchDialog always sent.
      producedQuantityVariancePresent: row.producedQtyVariancePresent ?? false,
      producedQtyMoreThanUpperLimit: row.producedQtyMoreThanUpperLimit ?? false,
      statDateVariancePresent: row.statDateVariancePresent ?? false,
      earlyPODateUpdateInERP: row.earlyPODateUpdateInERP ?? false,
      earlyDateUpdateType: row.earlyDateUpdateType ?? null,
      laterPODateUpdateInERP: row.laterPODateUpdateInERP ?? false,
      laterDateUpdateType: row.laterDateUpdateType ?? null,
      lessProducedQtyUpdateInERP: row.lessProducedQtyUpdateInERP ?? false,
      lessProducedQtyUpdateType: row.lessProducedQtyUpdateType ?? null,
      moreProducedQtyUpdateInERP: row.moreProducedQtyUpdateInERP ?? false,
      moreProducedQtyUpdateType: row.moreProducedQtyUpdateType ?? null,
      batchDetailsList: [entry],
    };
    const source = axios.CancelToken.source();
    approveCancelRef.current = source;
    axios
      .post(roleUrl("updateDeliveryScheduleBatch"), payload, { cancelToken: source.token })
      .then((res) => {
        if (!mountedRef.current) return;
        if (res.data && res.data.status) {
          // Button state -> "Approved" alongside the toast (slide 19).
          setApproved(true);
          // Approval advances the batch (40 → 39), so poll for the status change
          // after the blocking success toast.
          feedback.notifySuccess({
            message: "Variance/Mismatch Approved Successfully",
            statusChanging: true,
            batchId: row.schrockenDeliveryScheduleBatchID,
            prevStatus: row.SASLBatchStatusID,
            refetch: onRefresh,
          });
        } else {
          feedback.notifyError();
        }
        setApproving(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        feedback.notifyError();
        setApproving(false);
      });
  };

  const varianceTab = () => (
    <React.Fragment>
      {loading ? (
        <Box display="flex" justifyContent="center" p={2}><CircularProgress size={22} /></Box>
      ) : (
        <TableContainer component={Paper} style={{ boxShadow: "none", border: "1px solid #E0E4EA" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCellHead}>BMS Material Code / Description</TableCell>
                <TableCell className={classes.tableCellHead} align="right">BoM Quantity</TableCell>
                <TableCell className={classes.tableCellHead}>UOM</TableCell>
                <TableCell className={classes.tableCellHead}>Batch Numbers</TableCell>
                <TableCell className={classes.tableCellHead} align="right">Planned Allocated Qty</TableCell>
                <TableCell className={classes.tableCellHead} align="right">Actual Consumed Qty</TableCell>
                {showVarianceCol && <TableCell className={classes.tableCellHead} align="right">Consumed Qty Variance (%)</TableCell>}
                {showMismatchCol && <TableCell className={classes.tableCellHead} align="center">Batches Mismatch</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {alloc.map((c) => {
                const planned = c.plannedLotNumberAllocationDetails || [];
                const actual = c.actualConsumptionDetails || [];
                // Merge planned and actual by LOT NUMBER, not array index — a lot
                // can be planned but never consumed, or consumed from a different
                // lot than the one planned (this batch's mismatch case). Matches
                // VarianceMismatchDialog's buildConsumedRows so both dashboards
                // show the same rows for the same batch.
                const lotMap = {};
                planned.forEach((p) => {
                  const key = p.lotNumber || "unknown";
                  lotMap[key] = { batchNumber: p.lotNumber || "", plannedQty: p.plannedLotAllocation || 0, actualQty: 0 };
                });
                actual.forEach((a) => {
                  const key = a.componentBatchNumber || "unknown";
                  if (lotMap[key]) {
                    lotMap[key].actualQty = a.BomComponentConsumedQuantity || 0;
                  } else {
                    lotMap[key] = { batchNumber: a.componentBatchNumber || "", plannedQty: 0, actualQty: a.BomComponentConsumedQuantity || 0 };
                  }
                });
                const lots = Object.values(lotMap);
                const rowsN = Math.max(lots.length, 1);
                // Material Code/Description, BoM Quantity, UOM, Consumed Qty
                // Variance and Batches Mismatch are one value per MATERIAL, not
                // per lot — rowSpan them across all of this material's lot rows,
                // same as VarianceMismatchDialog, instead of repeating/blanking
                // them per row.
                return Array.from({ length: rowsN }).map((_, i) => {
                  const lot = lots[i];
                  return (
                    <TableRow key={`${c.componentCode}-${i}`}>
                      {i === 0 && (
                        <TableCell rowSpan={rowsN} style={{ verticalAlign: "middle" }}>
                          {c.componentCode} / {c.componentName}
                        </TableCell>
                      )}
                      {i === 0 && (
                        <TableCell rowSpan={rowsN} align="right" style={{ verticalAlign: "middle" }}>
                          {trimQty(c.BOMQuantity)}
                        </TableCell>
                      )}
                      {i === 0 && (
                        <TableCell rowSpan={rowsN} style={{ verticalAlign: "middle" }}>
                          {c.uom}
                        </TableCell>
                      )}
                      <TableCell>{lot ? lot.batchNumber : ""}</TableCell>
                      <TableCell align="right">{lot ? trimQty(lot.plannedQty) : ""}</TableCell>
                      <TableCell align="right">{lot ? trimQty(lot.actualQty) : ""}</TableCell>
                      {showVarianceCol && i === 0 && (
                        <TableCell rowSpan={rowsN} align="right" style={{ verticalAlign: "middle" }}>
                          {/* BCM-898 bug fix: matches VarianceMismatchDialog.jsx exactly —
                              red/bold when the variance exceeds qtyVarianceAcceptanceLimit
                              (fetched above), the warning triangle when the backend's own
                              quantityVariancePresent flag is set. Previously this cell was
                              plain text with neither check, so the red triangle never showed
                              here even when it did on the Batch Dashboard for the same data. */}
                          {(() => {
                            const variance = c.quantityVarianceValue;
                            if (variance === "NA" || variance === null || variance === undefined) return "";
                            if (variance === 0) return "0";
                            const displayValue = variance % 1 !== 0 ? Number(variance).toFixed(3) : variance;
                            const isOverLimit = qtyVarianceAcceptanceLimit != null
                              ? Math.abs(Number(variance)) > qtyVarianceAcceptanceLimit
                              : true;
                            return (
                              <span
                                style={
                                  isOverLimit
                                    ? { color: "#C0392B", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }
                                    : undefined
                                }
                              >
                                {displayValue}
                                {c.quantityVariancePresent === true && (
                                  <WarningRoundedIcon style={{ fontSize: 16 }} />
                                )}
                              </span>
                            );
                          })()}
                        </TableCell>
                      )}
                      {showMismatchCol && i === 0 && (
                        <TableCell rowSpan={rowsN} align="center" style={{ verticalAlign: "middle" }}>
                          {c.lotsMismatch ? (
                            <span style={{ color: "#C0392B", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                              YES <WarningRoundedIcon style={{ fontSize: 16 }} />
                            </span>
                          ) : (
                            "NO"
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </React.Fragment>
  );

  return (
    <SectionShell viewOnly={viewOnly}>
      {/* Multiple Variance/Mismatch Scenario — stacked one below the other, in
          this order: Stat Date, Consumed Batch, Produced Qty (design slides
          24-25). The shared Approve button lives inside whichever of these is
          last (approveHost), inside that section's own "Action Required" box. */}
      {showStatDateSection && (
        <Box mb={3}>
          <StatDateVarianceSection
            row={row}
            detail={detail}
            featuresAccess={featuresAccess}
            value={updateStatDate}
            onChange={setUpdateStatDate}
            onRefresh={onRefresh}
          />
        </Box>
      )}

      {showConsumedBatchSection && (
        <Box mb={showProducedQtySection ? 3 : 0}>
          <ActionTabs
            onActiveChange={setConsumedActiveTab}
            headRight={
              <React.Fragment>
                <SyncActions row={row} detail={detail} showSyncInventory showSyncPO resetsBatchPO resetsBatchInventory onRefresh={onRefresh} />
                <ResubmitDocumentButton row={row} detail={detail} featuresAccess={featuresAccess} onAfter={onRefresh} blue />
              </React.Fragment>
            }
            tabs={[
              { key: "variance", label: "Consumed Batch Variance/Mismatch", badge: mismatchCount || null, render: varianceTab },
              {
                key: "doc",
                label: "View Consumption Document",
                render: () => (
                  <ConsumptionDocumentTab row={row} detail={detail} featuresAccess={featuresAccess} onRefresh={onRefresh} />
                ),
              },
            ]}
          />
        </Box>
      )}

      {showProducedQtySection && (
        <Box mt={3}>
          <ProducedQtyVarianceSection
            row={row}
            detail={detail}
            featuresAccess={featuresAccess}
            viewOnly={viewOnly}
            onRefresh={onRefresh}
            value={updatePOQty}
            onChange={setUpdatePOQty}
            onActiveTabChange={setProducedQtyActiveTab}
          />
        </Box>
      )}

      {/* The single shared Approve button — a separate, common block below
          ALL the stacked sections, not nested inside any one of them. Hidden
          while either tab strip above is on a "View Document"-style tab.
          Flips to a green "Approved" state alongside the toast on success
          (slide 19: Button state -> "Approved"). */}
      {!onDocumentTab && (
        <React.Fragment>
          {error && <Typography style={{ color: "#C0392B", fontSize: "0.85rem", marginTop: 12 }}>{error}</Typography>}
          <Box mt={3}>
            {approved ? (
              <Button
                disabled
                startIcon={<CheckCircleIcon style={{ color: "#fff" }} />}
                style={{
                  backgroundColor: "#2E7D32", color: "#fff", borderRadius: 999,
                  padding: "8px 32px", textTransform: "none", fontWeight: 600,
                  opacity: 1,
                }}
              >
                Approved
              </Button>
            ) : (
              <Button className={classes.orangeBtn} onClick={handleApprove} disabled={!canApprove || approving}>
                {approving ? "Approving..." : "Approve"}
              </Button>
            )}
          </Box>
        </React.Fragment>
      )}
    </SectionShell>
  );
};

export default PA6VarianceMismatch;
